import { useTranslation } from "react-i18next";

import { useCenterMembers, useCenterPatients, useCenterUnits, useSessionPreferences } from "../../../center";
import type { CenterCourse } from "../../../center";
import { PastSessionImportModal, type ImportSubjectOption } from "../../../past_sessions";
import { WHOLE_CASELOAD } from "../../domain/caseloadPeople";
import { centerGroupLabel } from "../centerGroupLabel";

interface Props {
    centerId: string;
    onClose: () => void;
    /** A training centre's courses; none for a therapy centre. */
    courses?: CenterCourse[];
}

/**
 * The centre's history: its patients, students, couples and groups, its practising members to
 * credit each session to, and — at a school — the modules. Read once opened.
 */
export default function CenterPastSessionsImport({ centerId, onClose, courses }: Props) {
    // Every record the centre holds, whatever the list is filtered to.
    const { t } = useTranslation();
    const { patients } = useCenterPatients(centerId, { limit: WHOLE_CASELOAD });
    const { couples, groups } = useCenterUnits(centerId);
    const { members } = useCenterMembers(centerId, true);
    // Sessions only of the kinds the centre runs.
    const { modes } = useSessionPreferences();
    const courseName = (id?: string | null) => courses?.find((course) => course.id === id)?.internal_name;

    const subjects: ImportSubjectOption[] = [
        ...(modes.individual ? patients : [])
            .filter((patient) => !patient.is_anonymous)
            .map((patient) => ({
                kind: "patient" as const,
                id: patient.patient_id,
                // A student keeps one record per course: the course tells them apart.
                label: [`${patient.first_name} ${patient.last_name}`, courseName(patient.course_id)]
                    .filter(Boolean)
                    .join(" · "),
            })),
        ...(modes.couple ? couples : []).map((couple) => ({
            kind: "couple" as const,
            id: couple.couple_id,
            label: couple.display_name || couple.members.map((m) => `${m.first_name} ${m.last_name}`).join(" & "),
        })),
        ...(modes.group ? groups : []).map((group) => ({
            kind: "group" as const,
            id: group.group_id,
            // Its given name or its size, as the caseload calls it.
            label: centerGroupLabel(t, group.display_name, group.members.length),
        })),
    ];

    return (
        <PastSessionImportModal
            isOpen
            onClose={onClose}
            target={{ kind: "centre", centerId }}
            subjects={subjects}
            practitioners={members.map((member) => ({
                id: member.therapist_id,
                label: `${member.first_name} ${member.last_name}`,
            }))}
            courses={courses?.map((course) => ({
                id: course.id,
                label: course.internal_name,
                modules: course.phases.flatMap((phase) => (phase.id ? [{ id: phase.id, label: phase.name }] : [])),
            }))}
        />
    );
}
